/**
 * Parts removed from the middle of a video in the editor. The video is split at `splits`
 * (seconds); `removed` holds the time ranges left out of the preview and the export. Times are
 * source-video times, so annotations, zooms and voice-overs keep their times: anything inside a
 * removed part is left out with it, anything after it moves up with the video.
 */
export interface TimeRange {
  start: number;
  end: number;
}

export interface VideoCuts {
  splits: number[];
  removed: TimeRange[];
}

export const NO_CUTS: VideoCuts = { splits: [], removed: [] };

/** Closer than this to another split (or the ends) is not a new split. */
export const MIN_PART = 0.1;
/** Kept ranges shorter than this (under one frame) are dropped. */
const MIN_KEPT = 1 / 30;

const mergeRanges = (ranges: TimeRange[]): TimeRange[] => {
  const sorted = ranges
    .filter(r => Number.isFinite(r.start) && Number.isFinite(r.end) && r.end - r.start > 1e-3)
    .map(r => ({ start: Math.max(0, r.start), end: r.end }))
    .sort((a, b) => a.start - b.start);
  const out: TimeRange[] = [];
  for (const r of sorted) {
    const last = out[out.length - 1];
    if (last && r.start <= last.end + 1e-3) last.end = Math.max(last.end, r.end);
    else out.push({ ...r });
  }
  return out;
};

/** Valid, sorted cuts (also for drafts saved without them). */
export const normalizeCuts = (cuts?: Partial<VideoCuts> | null): VideoCuts => ({
  splits: [...new Set((cuts?.splits ?? []).filter(s => Number.isFinite(s) && s > 0))].sort((a, b) => a - b),
  removed: mergeRanges(cuts?.removed ?? []),
});

/** The part (between splits) that contains `t`. */
export const partAt = (cuts: VideoCuts, t: number, duration: number): TimeRange => {
  let start = 0;
  let end = duration;
  for (const s of cuts.splits) {
    if (s <= t) start = s;
    else { end = Math.min(end, s); break; }
  }
  return { start, end };
};

/** The removed range that contains `t`, if any. */
export const removedAt = (cuts: VideoCuts, t: number): TimeRange | null =>
  cuts.removed.find(r => t >= r.start && t < r.end) ?? null;

export const addSplit = (cuts: VideoCuts, t: number, duration: number): VideoCuts | null => {
  if (t < MIN_PART || t > duration - MIN_PART) return null;
  if (cuts.splits.some(s => Math.abs(s - t) < MIN_PART)) return null;
  return { ...cuts, splits: [...cuts.splits, t].sort((a, b) => a - b) };
};

export const removePart = (cuts: VideoCuts, part: TimeRange): VideoCuts =>
  ({ ...cuts, removed: mergeRanges([...cuts.removed, part]) });

export const restorePart = (cuts: VideoCuts, part: TimeRange): VideoCuts => ({
  ...cuts,
  removed: mergeRanges(cuts.removed.flatMap(r => [
    { start: r.start, end: Math.min(r.end, part.start) },
    { start: Math.max(r.start, part.end), end: r.end },
  ])),
});

/** What the export contains: [inPoint, outPoint] without the removed parts, in order. */
export const keptRanges = (cuts: VideoCuts, inPoint: number, outPoint: number): TimeRange[] => {
  const out: TimeRange[] = [];
  let start = inPoint;
  for (const r of cuts.removed) {
    if (r.end <= start || r.start >= outPoint) continue;
    if (r.start > start) out.push({ start, end: r.start });
    start = Math.max(start, r.end);
  }
  if (outPoint > start) out.push({ start, end: outPoint });
  return out.filter(r => r.end - r.start >= MIN_KEPT);
};

export const totalLength = (ranges: TimeRange[]) => ranges.reduce((sum, r) => sum + (r.end - r.start), 0);

/** Time in the export of source time `t` (seconds of kept video before it). */
export const exportTimeAt = (ranges: TimeRange[], t: number) =>
  ranges.reduce((sum, r) => sum + Math.max(0, Math.min(t, r.end) - r.start), 0);
